import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Stack, Title, Text, Badge, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useAuditFeed, AUDIT_SOURCES } from '../../../../data/useAuditFeed';
import { formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Not this person's activity: the tenant's.
 *
 * None of the five audit sources carries an actor id, so a user-scoped feed is
 * not possible today. That was previously admitted in a caption beside the
 * heading, where it read as a footnote to rows most operators would take as
 * things this person did. It is now the first thing on the panel.
 */
export function ActivityTab({ tenantId, tenantName }: { tenantId: string; tenantName?: string }) {
  const t = useT();
  const since = useMemo(() => new Date(Date.now() - 30 * 86_400_000).toISOString(), []);
  const enabled = useMemo(
    () => Object.fromEntries(AUDIT_SOURCES.map((s) => [s, true])) as Record<typeof AUDIT_SOURCES[number], boolean>,
    []
  );
  const { rows, loading } = useAuditFeed(since, enabled, 200);
  const scoped = useMemo(
    () => rows.filter((r) => r.organisation_id === tenantId).slice(0, 20),
    [rows, tenantId]
  );

  return (
    <Stack gap="lg">
      <Alert color="info">
        {t('users.view.activity.scopeWarning')}
      </Alert>
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline" gap="sm" wrap="wrap">
            <Title order={4}>{t('users.view.activity.title')}</Title>
            <Text fz="xs" c={MUTED}>
              {t('users.view.activity.hintLead')}{' '}
              <Link to={`/tenants/${tenantId}`} style={{ color: 'var(--rr-color-primary)' }}>
                {tenantName ?? t('users.view.activity.thisTenant')}
              </Link>.
            </Text>
          </Group>

          {loading && scoped.length === 0 ? (
            <Stack gap="sm">
              <Skeleton height={18} />
              <Skeleton height={18} />
              <Skeleton height={18} />
            </Stack>
          ) : scoped.length === 0 ? (
            <Text fz="sm" c={MUTED}>{t('users.view.activity.empty')}</Text>
          ) : (
            <Stack gap="sm">
              {scoped.map((row) => (
                <Group key={`${row.source}-${row.id}`} gap="sm" align="baseline" wrap="nowrap">
                  <Badge size="xs" variant="outline">{t(`auditSource.${row.source}`, row.source)}</Badge>
                  <Text fz="sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.summary}
                  </Text>
                  <Text fz="xs" c={MUTED} style={{ whiteSpace: 'nowrap' }}>
                    {formatRelative(row.occurred_at)}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
